import {
  useDataProvider,
  useGetIdentity,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import { DECEASED_ARTIST_TAG } from "@/lib/tina/artist-audit";
import type { IIdentity } from "@/providers/auth-provider";
import {
  ARTIST_AUDIT_KINDS,
  type ArtistAuditFindingRow,
  type ArtistAuditKind,
  type ArtistAuditRunResponse,
  type ArtistAuditSeverity,
  type ArtistAuditStatus,
} from "@/types/tina";

/** Cache key root for everything on this page that is not the Refine table itself. */
export const ARTIST_AUDIT_QUERY_ROOT = "tina-artist-audit";

export type ArtistAuditKindCounts = Record<ArtistAuditKind, number>;

export interface ArtistAuditCountScope {
  status: ArtistAuditStatus;
  severity: ArtistAuditSeverity | null;
}

/**
 * The two writes and the one derived read `/tina/artist-audit` needs, in the shape every
 * other Tina surface's repository hook takes: the data provider's `custom` for routes
 * Refine has no CRUD verb for, Sentry breadcrumbs around the mutation, and the error
 * surfaced through `toHumanErrorMessage` rather than swallowed.
 *
 * `/artist-audit/findings` itself is read by `useTable` through the `tina-artist-audit`
 * resource, not from here — the table owns pagination and `total`.
 */
export const useArtistAuditRepository = () => {
  const { custom } = useDataProvider()("tina");
  // The Mood Admin API: Approve writes the artist's `deceased` tag there, because tina's
  // API holds no Admin API credentials.
  const { getOne, update } = useDataProvider()();
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const { data: identity } = useGetIdentity<IIdentity>();

  /**
   * The signed-in curator's Firebase uid — the same value `resolveHitl` stamps as
   * `resolved_by` and `useEnrichmentIntake` as `requested_by`.
   *
   * `POST /artist-audit/findings/{id}/dismiss` authenticates the SERVICE, not the person,
   * so the body is the only place the actor can come from, and `dismissed_by` is
   * `min_length=1` — there is no "unknown" to fall back on. Writing anything but a uid
   * into an actor column is what put phantom "Former curators" on the IMS HITL
   * leaderboard, so an unloaded identity blocks the dismissal instead of inventing one.
   */
  const dismissedBy = identity?.id;

  /** Mark this page's rows and its count strip stale, so both refetch. */
  function invalidateFindings(): void {
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-artist-audit",
      invalidates: ["list"],
    });
    void queryClient.invalidateQueries({ queryKey: [ARTIST_AUDIT_QUERY_ROOT] });
  }

  async function dismiss(
    findingId: string,
    note: string | null,
  ): Promise<ArtistAuditFindingRow | null> {
    if (!dismissedBy) {
      open?.({
        type: "error",
        message: "Cannot dismiss yet",
        description:
          "Your account has not finished loading, and a dismissal has to be attributable. Reload the page and try again.",
      });
      return null;
    }
    Sentry.addBreadcrumb({
      category: "tina-artist-audit.dismiss",
      message: "Dismissing an artist-audit finding",
      level: "info",
      data: { findingId, hasNote: Boolean(note) },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { dismissed_by: dismissedBy, note: note || null },
        meta: {
          urlPath: `artist-audit/findings/${encodeURIComponent(findingId)}/dismiss`,
        },
      });
      invalidateFindings();
      open?.({
        type: "success",
        message: "Finding dismissed",
        description: "It will not come back unless the artists involved change.",
      });
      return response.data as ArtistAuditFindingRow;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to dismiss the finding",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  /**
   * Approve a "possibly deceased" card: the death is real.
   *
   * Two writes, in this order:
   * 1. Add `deceased` to the Mood artist's `tags` (`PUT /api/artists`). The route replaces
   *    the whole list, so the artist is read first and the tag is ADDED to what is there.
   *    Skipped when the tag is already there.
   * 2. `POST /artist-audit/findings/{id}/approve` — tina blocks the Spotify id (never the
   *    name) and closes the card.
   *
   * The tag goes first: if it fails, nothing changed. If step 2 fails after it, pressing
   * Approve again is safe — step 1 then skips and step 2 is idempotent.
   *
   * Not done: the artist stays on future events that already bill them.
   */
  async function approve(
    finding: ArtistAuditFindingRow,
  ): Promise<ArtistAuditFindingRow | null> {
    if (!dismissedBy) {
      open?.({
        type: "error",
        message: "Cannot approve yet",
        description:
          "Your account has not finished loading, and an approval has to be attributable. Reload the page and try again.",
      });
      return null;
    }
    const artist = finding.evidence.artists?.[0];
    if (!artist) {
      open?.({
        type: "error",
        message: "Cannot approve this card",
        description: "It names no artist.",
      });
      return null;
    }
    Sentry.addBreadcrumb({
      category: "tina-artist-audit.approve",
      message: "Approving a deceased-artist finding",
      level: "info",
      data: { findingId: finding.id, artistId: artist.artist_id },
    });
    try {
      // `getOne` resolves the flat ArtistDbDto, as in the artist repository.
      const stored = (await getOne({
        resource: "artists",
        id: artist.artist_id,
      })) as unknown as { tags?: string[] | null };
      const tags = Array.isArray(stored?.tags) ? stored.tags : [];
      if (!tags.includes(DECEASED_ARTIST_TAG)) {
        await update({
          resource: "artists",
          id: artist.artist_id,
          variables: {
            id: artist.artist_id,
            tags: [...tags, DECEASED_ARTIST_TAG],
          },
        });
      }
      const response = await custom!({
        url: "",
        method: "post",
        payload: { approved_by: dismissedBy },
        meta: {
          urlPath: `artist-audit/findings/${encodeURIComponent(finding.id)}/approve`,
        },
      });
      invalidateFindings();
      open?.({
        type: "success",
        message: "Approved as deceased",
        description: `${artist.name} is tagged "${DECEASED_ARTIST_TAG}" and blocked. Events that already list them keep them.`,
      });
      return response.data as ArtistAuditFindingRow;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to approve the finding",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  /**
   * How many findings each kind holds inside the current status/severity view.
   *
   * One request per kind with `limit=1`, reading `total` off each — the route counts over the same
   * WHERE it pages, so each total is exact. Counted rather than tallied from the loaded
   * page because the first real backlog is ~1,400 findings and a page holds 50: a
   * client-side tally would tell the curator that `duplicate_name` has 43 of them.
   */
  async function countsByKind({
    status,
    severity,
  }: ArtistAuditCountScope): Promise<ArtistAuditKindCounts> {
    const totals = await Promise.all(
      ARTIST_AUDIT_KINDS.map(async (kind) => {
        const response = await custom!({
          url: "",
          method: "get",
          query: {
            status,
            kind,
            limit: 1,
            offset: 0,
            ...(severity ? { severity } : {}),
          },
          meta: { urlPath: "artist-audit/findings" },
        });
        const body = response.data as { total?: number };
        return [kind, body.total ?? 0] as const;
      }),
    );
    return Object.fromEntries(totals) as ArtistAuditKindCounts;
  }

  /**
   * START the audit now, ignoring its once-a-day guard. Does not wait for it.
   *
   * The audit runs in tina's `tina-artist-audit` Cloud Run Job, because a
   * ~96,128-document Firestore read cannot finish inside a 60s HTTP request — so this
   * returns a Job execution, not counts. Read-only against Mood either way: the audit
   * re-reads the artist catalog, re-runs the five checks and writes only tina's own
   * findings table. It is how a curator confirms an artist they repaired has stopped being
   * a finding, without waiting for tomorrow.
   *
   * The three notifications match the three things that can happen, and none of them
   * claims a result: "started" is not "finished", and saying otherwise about a run whose
   * numbers nobody has seen yet is the same manufactured confidence the server-side
   * short-catalog floor exists to prevent.
   */
  async function runNow(): Promise<ArtistAuditRunResponse> {
    Sentry.addBreadcrumb({
      category: "tina-artist-audit.run",
      message: "Starting the artist audit job",
      level: "info",
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: {},
        meta: { urlPath: "artist-audit/run" },
      });
      const outcome = response.data as ArtistAuditRunResponse;
      // Invalidated even though nothing has changed YET: the run takes a couple of
      // minutes, so this only guarantees the table is not still showing a page cached
      // from before the curator's own last dismissal. The refresh that matters is the
      // one they do after the run finishes.
      invalidateFindings();
      if (outcome.kind === "dispatched") {
        open?.({
          type: "success",
          message: "Audit started",
          description:
            "It takes a couple of minutes. Refresh this page when it is done — nothing here updates on its own.",
        });
      } else if (outcome.kind === "noop") {
        open?.({
          type: "error",
          message: "Audit not started",
          description:
            "The audit job is not configured on this environment, so there was nothing to start.",
        });
      } else {
        open?.({
          type: "error",
          message: "Failed to start the audit",
          description:
            outcome.error_message ??
            "The audit job could not be started and gave no reason.",
        });
      }
      return outcome;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to start the audit",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  return { dismiss, approve, countsByKind, runNow, dismissedBy };
};
